"use client";

import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  VStack,
  Heading,
  Text,
  useToast,
  Image,
  HStack,
} from "@chakra-ui/react";
import OTPInput from "./OTPInput.tsx";
import { supabase } from "../supabase.ts";
import { useAppTheme } from "../ThemeContext.tsx";

const OTPVerification = () => {
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();

  // Define colors based on theme
  const outerBg = isDark ? "gray.800" : "gray.100";
  const innerBg = isDark ? "gray.700" : "white";
  const headingColor = isDark ? "white" : "black";
  const textColor = isDark ? "gray.300" : "gray.600";

  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const email = location.state?.email;
  const dashboardIconUrl = process.env.REACT_APP_MAIN_LOGO_URL || "";

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0 && resendDisabled) {
      setResendDisabled(false);
    }
  }, [countdown, resendDisabled]);

  const handleOTPComplete = (otpValue: string) => {
    setOtp(otpValue);
  };

  const handleVerify = async () => {
    if (!email) {
      toast({
        title: "Error",
        description: "Email information is missing",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (otp.length !== 6) {
      toast({
        title: "Error",
        description: "Please enter a valid 6-digit OTP",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);

    try {
      // This is a placeholder for your actual OTP verification logic
      // You would need to implement the actual verification with Supabase or your backend
      const { error } = await supabase.auth.verifyOtp({
        email,
        token: otp,
        type: "signup",
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Your account has been verified successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Redirect to login page after successful verification
      navigate("/login");
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to verify OTP",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (!email) {
      toast({
        title: "Error",
        description: "Email information is missing",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setResendDisabled(true);
    setCountdown(60); // 60 seconds cooldown

    try {
      // This is a placeholder for your actual resend OTP logic
      // You would need to implement the actual resend with Supabase or your backend
      const { error } = await supabase.auth.resend({
        type: "signup",
        email,
      });

      if (error) throw error;

      toast({
        title: "OTP Resent",
        description: "A new OTP has been sent to your email",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to resend OTP",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      setResendDisabled(false);
      setCountdown(0);
    }
  };

  if (!email) {
    return (
      <Box
        minHeight="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg={outerBg}
      >
        <Box
          width="full"
          maxWidth="md"
          p={8}
          borderRadius="md"
          bg={innerBg}
          boxShadow="lg"
        >
          <VStack spacing={6}>
            {dashboardIconUrl && (
              <Image
                src={dashboardIconUrl || "/placeholder.svg"}
                alt="Logo"
                maxH="60px"
                mb={4}
              />
            )}
            <Heading color={headingColor}>Error</Heading>
            <Text color={textColor}>Email information is missing.</Text>
            <Button
              onClick={() => navigate("/login")}
              colorScheme="gray"
              _hover={{ bg: isDark ? "gray.600" : "gray.300" }}
              background={isDark ? "gray.700" : "gray.200"}
              textColor={isDark ? "whiteAlpha" : "gray.800"}
            >
              Back to Login
            </Button>
          </VStack>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      minHeight="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bg={outerBg}
    >
      <Box
        width="full"
        maxWidth="md"
        p={8}
        borderRadius="md"
        bg={innerBg}
        boxShadow="lg"
      >
        <VStack spacing={6}>
          {dashboardIconUrl && (
            <Image
              src={dashboardIconUrl || "/placeholder.svg"}
              alt="Logo"
              maxH="60px"
              mb={4}
            />
          )}
          <Heading color={headingColor}>Verify Your Email</Heading>
          <Text color={textColor} textAlign="center">
            We've sent a verification code to {email}. Please enter the code
            below.
          </Text>

          <OTPInput onComplete={handleOTPComplete} />

          <Button
            onClick={handleVerify}
            colorScheme="gray"
            _hover={{ bg: isDark ? "gray.600" : "gray.300" }}
            background={isDark ? "gray.700" : "gray.200"}
            textColor={isDark ? "whiteAlpha" : "gray.800"}
            width="full"
            isLoading={loading}
          >
            Verify
          </Button>

          <HStack spacing={2}>
            <Text color={textColor}>Didn't receive the code?</Text>
            <Button
              variant="link"
              onClick={handleResendOTP}
              isDisabled={resendDisabled}
              color={isDark ? "blue.300" : "blue.600"}
            >
              {resendDisabled ? `Resend in ${countdown}s` : "Resend OTP"}
            </Button>
          </HStack>

          <Button
            variant="outline"
            onClick={() => navigate("/login")}
            color={textColor}
            borderColor={textColor}
          >
            Back to Login
          </Button>
        </VStack>
      </Box>
    </Box>
  );
};

export default OTPVerification;
